import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Loader2 } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { getDocumentDownloadUrl, getLibrary } from "../api/rag-api.js";
import { mapLibrary, mapPublicLibrary } from "../api/mappers.js";
import { DocumentViewerPanel } from "../components/DocumentViewerPanel.js";
import {
  buildDocumentsListPath,
  isPdfDocument,
  parsePageNumber,
  type DocumentViewerContext,
} from "../lib/document-open.js";

export function DocumentViewerPage() {
  const navigate = useNavigate();
  const { workspaceId, libraryId = "", documentId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const [title, setTitle] = useState("Document");
  const [mimeType, setMimeType] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const context = useMemo<DocumentViewerContext | null>(() => {
    if (!libraryId) return null;
    if (workspaceId) {
      return { scope: "km", workspaceId, libraryId };
    }
    return { scope: "regulatory", libraryId };
  }, [workspaceId, libraryId]);

  const initialPage = parsePageNumber(searchParams.get("page") ?? undefined) ?? 1;
  const documentsPath = context ? buildDocumentsListPath(context) : "/";

  useEffect(() => {
    if (!documentId || !libraryId) return;

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const [{ library: libraryData }, downloadMeta] = await Promise.all([
          getLibrary(libraryId),
          getDocumentDownloadUrl(documentId),
        ]);

        if (cancelled) return;

        if (context?.scope === "regulatory") {
          mapPublicLibrary(libraryData);
        } else {
          mapLibrary(libraryData);
        }

        setTitle(downloadMeta.title || "Document");
        setMimeType(downloadMeta.mimeType);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load document");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [context?.scope, documentId, libraryId]);

  async function handleOpenExternal() {
    try {
      const { downloadUrl } = await getDocumentDownloadUrl(documentId);
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to open document");
    }
  }

  if (loading) {
    return (
      <section className="flex flex-1 items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
      </section>
    );
  }

  if (error) {
    return (
      <section className="flex flex-1 flex-col items-center justify-center gap-4 p-6">
        <p className="text-sm text-red-300">{error}</p>
        <Button variant="outline" onClick={() => navigate(documentsPath)}>
          Back to documents
        </Button>
      </section>
    );
  }

  if (isPdfDocument(mimeType, title)) {
    return (
      <DocumentViewerPanel
        document={{ documentId, title, initialPage }}
        onClose={() => navigate(documentsPath)}
      />
    );
  }

  return (
    <section className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="max-w-md text-sm text-slate-300">
        In-app preview is available for PDF files only. Open this document in a new tab to view
        it.
      </p>
      <div className="flex gap-2">
        <Button variant="outline" onClick={() => navigate(documentsPath)}>
          Back to documents
        </Button>
        <Button variant="default" className="gap-2" onClick={() => void handleOpenExternal()}>
          <ExternalLink className="h-4 w-4" />
          Open in new tab
        </Button>
      </div>
    </section>
  );
}
